import { createFileRoute, Link } from "@tanstack/react-router";
import { Briefcase, LifeBuoy, Mail, MessageSquare, Sparkles } from "lucide-react";

import { SALES_EMAIL, SUPPORT_EMAIL } from "@/lib/contact";
import { createWebPageJsonLd, seo } from "@/lib/seo";

export const Route = createFileRoute("/(landing)/contact/")({
	component: ContactPage,
	head: ({ match }) => {
		const title = "Contact us | StudyOS";
		const description =
			"Get in touch with the StudyOS team — support, sales, partnerships, and general inquiries.";

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["StudyOS contact", "StudyOS support", "StudyOS sales"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

type ContactCardProps = {
	icon: typeof Mail;
	title: string;
	description: string;
	email: string;
	cta: string;
};

function ContactCard({ icon: Icon, title, description, email, cta }: ContactCardProps) {
	return (
		<div className="group relative flex flex-col rounded-2xl border border-[#0e1330]/10 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#0e1330]/30 hover:shadow-lg">
			<div className="mb-4 inline-flex w-fit rounded-xl bg-[#ffc93c]/20 p-3">
				<Icon className="size-6 text-[#0e1330]" />
			</div>
			<h3 className="mb-2 text-lg font-semibold text-[#0e1330]">{title}</h3>
			<p className="mb-6 text-sm leading-relaxed text-[#0e1330]/70">{description}</p>
			<a
				href={`mailto:${email}`}
				className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-[#ffc93c] px-5 py-2.5 text-sm font-semibold text-[#0e1330] transition-shadow hover:shadow-md"
			>
				{cta}
			</a>
			<p className="mt-3 text-xs text-[#0e1330]/50">{email}</p>
		</div>
	);
}

function ContactPage() {
	const cards: ContactCardProps[] = [
		{
			icon: LifeBuoy,
			title: "Product support",
			description:
				"Questions about your account, billing, or how a feature works? Our support team usually replies within one business day.",
			email: SUPPORT_EMAIL,
			cta: "Email support",
		},
		{
			icon: Briefcase,
			title: "Sales & partnerships",
			description:
				"Bringing StudyOS to a school, university, or team? We'll help you pick the right plan and onboard your group.",
			email: SALES_EMAIL,
			cta: "Talk to sales",
		},
	];

	return (
		<main className="min-h-screen">
			{/* Hero */}
			<section className="relative flex items-center justify-center overflow-hidden px-4 pt-24 pb-12 md:pt-32 md:pb-16">
				<div className="absolute inset-0 overflow-hidden" aria-hidden="true">
					<div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-[#ffc93c]/20 blur-3xl" />
					<div className="absolute right-1/4 bottom-0 h-80 w-80 rounded-full bg-[#0e1330]/5 blur-3xl" />
				</div>

				<div className="relative z-10 mx-auto max-w-3xl text-center">
					<div className="mb-6 flex justify-center">
						<div className="inline-flex items-center gap-2 rounded-full border border-[#0e1330]/15 bg-white px-4 py-1.5 text-sm text-[#0e1330]">
							<Sparkles className="size-4 text-[#ffc93c]" />
							<span>We'd love to hear from you</span>
						</div>
					</div>

					<h1 className="mb-6 text-4xl font-bold tracking-tight text-[#0e1330] md:text-5xl lg:text-6xl">
						Get in touch
					</h1>

					<p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#0e1330]/70 md:text-xl">
						Pick the channel that fits — support, sales, or just a quick hello. We read
						every message.
					</p>
				</div>
			</section>

			{/* Contact channels */}
			<section className="px-4 py-8 md:py-12">
				<div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2">
					{cards.map((card) => (
						<ContactCard key={card.email} {...card} />
					))}
				</div>
			</section>

			{/* General inquiries */}
			<section className="px-4 py-12 md:py-16">
				<div className="mx-auto max-w-3xl rounded-2xl border border-[#0e1330]/10 bg-white p-8 md:p-10">
					<div className="mb-4 inline-flex rounded-xl bg-[#ffc93c]/20 p-3">
						<MessageSquare className="size-6 text-[#0e1330]" />
					</div>
					<h2 className="mb-3 text-2xl font-bold tracking-tight text-[#0e1330] md:text-3xl">
						Anything else
					</h2>
					<p className="mb-6 leading-relaxed text-[#0e1330]/70">
						Press, content collaborations, accessibility feedback, or general questions
						— send us a note and the right person will follow up.
					</p>
					<a
						href={`mailto:${SUPPORT_EMAIL}`}
						className="inline-flex items-center gap-2 rounded-full border border-[#0e1330]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#0e1330] transition-colors hover:border-[#0e1330]/40"
					>
						<Mail className="size-4" />
						{SUPPORT_EMAIL}
					</a>
				</div>
			</section>

			{/* CTA */}
			<section className="px-4 py-16 md:py-24">
				<div className="mx-auto max-w-2xl text-center">
					<p className="mb-6 text-[#0e1330]/70">
						Not ready to write yet? Explore StudyOS first.
					</p>
					<Link
						to="/"
						className="inline-flex items-center gap-2 rounded-full bg-[#ffc93c] px-8 py-3 font-semibold text-[#0e1330] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
					>
						Back to home
					</Link>
				</div>
			</section>
		</main>
	);
}
